import type { PendingReposicionRow, ReposicionListItem } from '../domain/models/ReposicionCatalog.ts';
import {
  formatReposicionDeliveryReadinessMessage,
  formatReposicionDeliveryWarningMessage,
  reposicionDeliveryMediaWarnings,
  reposicionDeliveryReadinessIssues,
  type ReposicionDeliveryReadinessInput,
  type ReposicionDeliveryReadinessIssue,
} from '../domain/reposicionDeliveryReadiness.ts';
import { blobHasSignatureInk } from '@src/modules/platform/signature/application/signatureBlobInk.ts';
import type { ReposicionApplicationService } from './ReposicionApplicationService.ts';

async function reposicionLocalFotoLoaded(service: ReposicionApplicationService, mediaId: string): Promise<boolean> {
  const id = mediaId.trim();
  if (!id) return false;
  const blob = await service.media.get(id);
  return Boolean(blob?.size);
}

async function reposicionLocalFirmaLoaded(service: ReposicionApplicationService, mediaId: string): Promise<boolean> {
  const id = mediaId.trim();
  if (!id) return false;
  const blob = await service.media.get(id);
  if (!blob?.size) return false;
  const ink = await blobHasSignatureInk(blob);
  return ink === true;
}

async function appendMissingLocalMediaWarnings(
  service: ReposicionApplicationService,
  input: ReposicionDeliveryReadinessInput,
  issues: ReposicionDeliveryReadinessIssue[],
): Promise<void> {
  const label = input.beneficiarioNombre?.trim() || input.beneficiarioDocumento?.trim() || 'Reposición';
  if (input.firmaMediaId?.trim() && !(await reposicionLocalFirmaLoaded(service, input.firmaMediaId))) {
    issues.push({ message: `${label} sin firma (archivo local faltante).` });
  }
  if (input.fotoMediaId?.trim() && !(await reposicionLocalFotoLoaded(service, input.fotoMediaId))) {
    issues.push({ message: `${label} sin foto (archivo local faltante).` });
  }
}

async function collectReposicionDeliveryMediaWarnings(
  service: ReposicionApplicationService,
  input: ReposicionDeliveryReadinessInput,
): Promise<ReposicionDeliveryReadinessIssue[]> {
  const issues = reposicionDeliveryMediaWarnings(input);
  await appendMissingLocalMediaWarnings(service, input, issues);
  return issues;
}

export function reposicionDeliveryReadinessInputFromPending(row: PendingReposicionRow): ReposicionDeliveryReadinessInput {
  return {
    beneficiarioNombre: row.beneficiarioNombre,
    beneficiarioDocumento: row.beneficiarioDocumento,
    firmaMediaId: row.firmaMediaId,
    fotoMediaId: row.fotoMediaId,
  };
}

export async function loadReposicionDeliveryReadinessInput(
  service: ReposicionApplicationService,
  publicId: string,
): Promise<ReposicionDeliveryReadinessInput | null> {
  const record = await service.reposiciones.getForEdit(publicId);
  if (!record) return null;
  return {
    beneficiarioNombre: record.beneficiarioNombre,
    beneficiarioDocumento: record.beneficiarioDocumento,
    firmaMediaId: record.firmaMediaId,
    fotoMediaId: record.fotoMediaId,
  };
}

export async function checkReposicionDeliveryReady(
  service: ReposicionApplicationService,
  opts: { publicId: string; beneficiarioNombre?: string; action: 'pdf' | 'sync' },
): Promise<{ ok: true; warning?: string } | { ok: false; message: string }> {
  const input = await loadReposicionDeliveryReadinessInput(service, opts.publicId);
  if (!input) return { ok: false, message: 'No se pudo cargar la reposición.' };
  const issues = reposicionDeliveryReadinessIssues(input);
  if (issues.length > 0) {
    return {
      ok: false,
      message: formatReposicionDeliveryReadinessMessage(issues, {
        beneficiarioNombre: opts.beneficiarioNombre,
        action: opts.action,
      }),
    };
  }
  const warnings = await collectReposicionDeliveryMediaWarnings(service, input);
  const warning =
    warnings.length > 0
      ? formatReposicionDeliveryWarningMessage(warnings, { beneficiarioNombre: opts.beneficiarioNombre })
      : undefined;
  return warning ? { ok: true, warning } : { ok: true };
}

export async function checkReposicionDeliveryReadyFromPending(
  service: ReposicionApplicationService,
  row: PendingReposicionRow,
): Promise<{ ok: true; warning?: string } | { ok: false; message: string }> {
  const input = reposicionDeliveryReadinessInputFromPending(row);
  const issues = reposicionDeliveryReadinessIssues(input);
  if (issues.length > 0) {
    return {
      ok: false,
      message: formatReposicionDeliveryReadinessMessage(issues, {
        action: 'sync',
        beneficiarioNombre: row.beneficiarioNombre,
      }),
    };
  }
  const warnings = await collectReposicionDeliveryMediaWarnings(service, input);
  const warning =
    warnings.length > 0
      ? formatReposicionDeliveryWarningMessage(warnings, { beneficiarioNombre: row.beneficiarioNombre })
      : undefined;
  return warning ? { ok: true, warning } : { ok: true };
}

export async function checkReposicionListItemDeliveryReady(
  service: ReposicionApplicationService,
  row: ReposicionListItem,
  action: 'pdf' | 'sync',
): Promise<{ ok: true; warning?: string } | { ok: false; message: string }> {
  return checkReposicionDeliveryReady(service, {
    publicId: row.publicId,
    beneficiarioNombre: row.beneficiarioNombre,
    action,
  });
}
